import { useCallback, useEffect, useState } from "react";
import { Database, Plus, TestTube, Pencil, Trash2, Table as TableIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { toast } from "sonner";
import SchemaExplorerSheet from "../components/datasources/SchemaExplorerSheet";

const KIND_LABEL = { postgres: "PostgreSQL", mysql: "MySQL", sqlite: "SQLite" };

export default function Datasources() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const t = useT();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [schemaDs, setSchemaDs] = useState(null);
  const [showSchema, setShowSchema] = useState(false);

  function emptyForm() {
    return {
      name: "",
      kind: "postgres",
      host: "",
      port: 5432,
      database: "",
      username: "",
      password: "",
      company_id: "",
    };
  }
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data }, c] = await Promise.all([
      api.get("/datasources"),
      isAdmin ? api.get("/companies").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setItems(data || []);
    setCompanies(c.data || []);
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  }
  function openEdit(ds) {
    setEditing(ds);
    setForm({
      name: ds.name,
      kind: ds.kind,
      host: ds.host || "",
      port: ds.port || (ds.kind === "postgres" ? 5432 : 3306),
      database: ds.database || "",
      username: ds.username || "",
      password: "",
      company_id: ds.company_id || "",
    });
    setShowForm(true);
  }

  async function save() {
    if (!form.name || !form.kind || !form.database)
      return toast.error("Name, kind and database are required");
    const payload = { ...form };
    if (!isAdmin) delete payload.company_id;
    if (!payload.company_id) payload.company_id = null;
    try {
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.patch(`/datasources/${editing.id}`, payload);
        toast.success("Datasource updated");
      } else {
        await api.post("/datasources", payload);
        toast.success("Datasource created");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function testCurrent() {
    try {
      const { data } = await api.post("/datasources/test-config", { ...form, company_id: null });
      if (data.ok) toast.success("Connection ok");
      else toast.error(`Connection failed: ${data.message}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function testRow(id) {
    try {
      const { data } = await api.post(`/datasources/${id}/test`);
      if (data.ok) toast.success("Connection ok");
      else toast.error(`Connection failed: ${data.message}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete datasource?")) return;
    try {
      await api.delete(`/datasources/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="datasources-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="kpi-label">{t("nav.datasources")}</p>
          <h1 className="text-2xl font-bold tracking-tight">{t("ds.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("ds.subtitle")}</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="new-datasource-btn">
            <Plus className="h-4 w-4 mr-1.5" /> {t("ds.new")}
          </Button>
        )}
      </div>

      <Card className="card-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("ds.kind")}</TableHead>
                <TableHead>{t("ds.host")} / {t("ds.database")}</TableHead>
                <TableHead>{t("ds.username")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t("common.loading")}</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">{t("ds.no_datasources")}</TableCell></TableRow>
              ) : (
                items.map((d) => (
                  <TableRow key={d.id} data-testid={`datasource-row-${d.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Database className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium">{d.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono uppercase text-[10px]">{KIND_LABEL[d.kind] || d.kind}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {d.host ? `${d.host}:${d.port || ""} / ${d.database}` : d.database}
                    </TableCell>
                    <TableCell className="text-xs">{d.username || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setSchemaDs(d);
                            setShowSchema(true);
                          }}
                          data-testid={`browse-ds-${d.id}`}
                          title="Browse schema"
                        >
                          <TableIcon className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => testRow(d.id)} data-testid={`test-ds-${d.id}`}>
                              <TestTube className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(d)} data-testid={`edit-ds-${d.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(d.id)} data-testid={`delete-ds-${d.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="datasource-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("ds.edit") : t("ds.new")}</DialogTitle>
            <DialogDescription>{t("ds.encrypted")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="dsn">{t("common.name")}</Label>
              <Input id="dsn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ds-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("ds.kind")}</Label>
              <Select
                value={form.kind}
                onValueChange={(v) =>
                  setForm({ ...form, kind: v, port: v === "postgres" ? 5432 : v === "mysql" ? 3306 : null })
                }
              >
                <SelectTrigger data-testid="ds-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="postgres">PostgreSQL</SelectItem>
                  <SelectItem value="mysql">MySQL</SelectItem>
                  <SelectItem value="sqlite">SQLite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>{t("ds.optional_company")}</Label>
                <Select value={form.company_id || "_none"} onValueChange={(v) => setForm({ ...form, company_id: v === "_none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder={t("ds.global")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">{t("ds.global")}</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.kind !== "sqlite" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="dsh">{t("ds.host")}</Label>
                  <Input id="dsh" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="localhost" data-testid="ds-host" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dsp">{t("ds.port")}</Label>
                  <Input id="dsp" type="number" value={form.port || ""} onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || null })} data-testid="ds-port" />
                </div>
              </>
            )}
            <div className={form.kind === "sqlite" ? "col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label htmlFor="dsd">{form.kind === "sqlite" ? t("ds.file_path") : t("ds.database")}</Label>
              <Input id="dsd" value={form.database} onChange={(e) => setForm({ ...form, database: e.target.value })} placeholder={form.kind === "sqlite" ? "/path/to/file.db" : "mydb"} data-testid="ds-database" />
            </div>
            {form.kind !== "sqlite" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="dsu">{t("ds.username")}</Label>
                  <Input id="dsu" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="ds-username" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dspw">{t("common.password")}{editing && ` ${t("ds.password_keep")}`}</Label>
                  <Input id="dspw" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ds-password" />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="flex-row sm:justify-between sm:space-x-0">
            <Button variant="outline" onClick={testCurrent} data-testid="ds-test-config">
              <TestTube className="h-4 w-4 mr-1.5" /> {t("ds.test_connection")}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
              <Button onClick={save} data-testid="ds-save">{editing ? t("common.save") : t("common.create")}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SchemaExplorerSheet
        open={showSchema}
        onOpenChange={setShowSchema}
        datasource={schemaDs}
      />
    </div>
  );
}
